import React, { useCallback, useEffect } from 'react';
import { CustomFetchAudioForPlayingEffect } from 'app/store/Shares/slice.types';
import { useAppDispatch, useAppSelector } from 'app/store';
import { getAudioPlayer } from 'app/store/AudioPlayer/selectors';
import {
  setIsPlayerReadyAction,
  setIsPlayingAction,
} from 'app/store/AudioPlayer/slice';
import { fetchWaveformFeatureFlag } from 'app/store/AudioPlayer/effects';
import { PlaylistImage } from 'types/playlists.types';
import { AudioPlayerComponent } from './components/AudioPlayerComponent';
import { useAudioPlayer } from './hooks';
import { useAudio } from './lib';

export interface AudioPlayerProps {
  transitionType?: 'persistent' | 'fixed';
  isWaveformFeatureEnabled?: boolean;
  isPlayerExpandable?: boolean;
  customFetchAudioForPlayingEffect?: CustomFetchAudioForPlayingEffect;
  songCover?: boolean;
  images?: PlaylistImage[];
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  transitionType = 'persistent',
  isWaveformFeatureEnabled: isWaveformFeatureEnabledProp,
  isPlayerExpandable = true,
  customFetchAudioForPlayingEffect,
  songCover,
  images: imagesProp,
}) => {
  const {
    isOpen,
    isPlaying,
    isWaveformOpen,
    playMode,
    isPlayerReady,
    queue,
    queueIndex,
    images = imagesProp,
  } = useAppSelector(getAudioPlayer);
  const dispatch = useAppDispatch();
  const { stop, setIsWaveformOpen } = useAudioPlayer();

  const { playbackEngine } = useAudio({
    customFetchAudioForPlayingEffect,
  });

  const setIsPlaying = useCallback(
    (isPlaying: boolean) => dispatch(setIsPlayingAction(isPlaying)),
    [dispatch]
  );

  const setIsPlayerReady = useCallback(
    (isReady: boolean) => dispatch(setIsPlayerReadyAction(isReady)),
    [dispatch]
  );

  useEffect(() => {
    dispatch(
      fetchWaveformFeatureFlag({
        isWaveformFeatureEnabled: isWaveformFeatureEnabledProp,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AudioPlayerComponent
      handleClose={stop}
      setIsPlaying={setIsPlaying}
      setIsPlayerReady={setIsPlayerReady}
      isPlaying={isPlaying}
      isOpen={isOpen}
      transitionType={transitionType}
      setIsWaveformOpen={setIsWaveformOpen}
      isWaveformOpen={isWaveformOpen}
      playMode={playMode}
      isPlayerReady={isPlayerReady}
      queue={queue}
      queueIndex={queueIndex}
      isPlayerExpandable={isPlayerExpandable}
      songCover={songCover}
      images={images}
      playbackEngine={playbackEngine}
    />
  );
};
